import { useEffect, useState } from 'preact/hooks';

const PREF_KEY = 'hexagon.push';

export type PushStatus = 'unsupported' | 'ios-install' | 'denied' | 'off' | 'on' | 'working';

function supported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** iOS only offers web push to sites added to the Home Screen. */
function iosNeedsInstall(): boolean {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone;
  return ios && !standalone;
}

function wanted(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) === 'on';
  } catch {
    return false;
  }
}

function setWanted(on: boolean): void {
  try {
    localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
  } catch {
    // Private mode: it just won't be remembered.
  }
}

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const pad = b64url.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((b64url.length + 3) % 4);
  const bin = atob(pad);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function subscription(create: boolean): Promise<PushSubscription | null> {
  const reg = (await navigator.serviceWorker.getRegistration('/')) ?? (create ? await navigator.serviceWorker.register('/sw.js') : null);
  if (!reg) return null;
  await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing || !create) return existing;
  const { publicKey } = (await (await fetch('/api/push/key')).json()) as { publicKey: string };
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
}

/**
 * Turn notifications for one room. Once switched on, every room you're seated
 * in gets this device's subscription when you connect, so it stays on for the
 * next game too.
 */
export function usePush(seated: boolean, subscribe: (sub: unknown) => void, unsubscribe: (endpoint: string) => void) {
  const initial = (): PushStatus => {
    if (iosNeedsInstall()) return 'ios-install';
    if (!supported()) return 'unsupported';
    if (Notification.permission === 'denied') return 'denied';
    return wanted() && Notification.permission === 'granted' ? 'on' : 'off';
  };
  const [status, setStatus] = useState<PushStatus>(initial);

  // Re-register with this room whenever we (re)take a seat.
  useEffect(() => {
    if (!seated || status !== 'on') return;
    subscription(false)
      .then((sub) => sub && subscribe(sub.toJSON()))
      .catch(() => {});
  }, [seated, status]);

  const enable = async () => {
    setStatus('working');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'off');
        return;
      }
      const sub = await subscription(true);
      if (!sub) throw new Error('no subscription');
      setWanted(true);
      subscribe(sub.toJSON());
      setStatus('on');
    } catch {
      setStatus('off');
    }
  };

  const disable = async () => {
    setWanted(false);
    setStatus('off');
    try {
      const sub = await subscription(false);
      if (sub) {
        unsubscribe(sub.endpoint);
        await sub.unsubscribe();
      }
    } catch {
      // Already gone.
    }
  };

  return { status, enable, disable };
}

export function PushToggle({ push }: { push: ReturnType<typeof usePush> }) {
  const { status, enable, disable } = push;
  if (status === 'unsupported') return null;
  if (status === 'ios-install') {
    return (
      <p class="muted small" data-testid="push-ios">
        To get turn notifications on iPhone: Share → Add to Home Screen, then open Hexagon from there.
      </p>
    );
  }
  return (
    <label class="check push-toggle" title="Get a notification when it's your move, even with the app closed">
      <input
        type="checkbox"
        data-testid="push-toggle"
        checked={status === 'on'}
        disabled={status === 'working' || status === 'denied'}
        onChange={(e) => ((e.target as HTMLInputElement).checked ? enable() : disable())}
      />
      <span>{status === 'denied' ? 'Notifications are blocked in your browser settings' : 'Notify me when it’s my move'}</span>
    </label>
  );
}
